import React, { FC, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Timeline from '@mui/lab/Timeline';
import Button from '@mui/material/Button';
import { GridRowId } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import TimelineDot from '@mui/lab/TimelineDot'
import ApiIcon from '@mui/icons-material/Api';
import GppBadIcon from '@mui/icons-material/GppBad';
import FlightIcon from '@mui/icons-material/Flight';
import GppGoodIcon from '@mui/icons-material/GppGood';
import WarningIcon from '@mui/icons-material/Warning';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import BusinessIcon from '@mui/icons-material/Business'
import TimelineContent from '@mui/lab/TimelineContent';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import ContentCopy from '@mui/icons-material/ContentCopy';
import GridOnIcon from '@mui/icons-material/GridOn';
import VerifiedIcon from '@mui/icons-material/Verified';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import { Box, Chip, Modal, Stack, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import DirectionsBoatFilledIcon from '@mui/icons-material/DirectionsBoatFilled';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import Product from '../../../../Models/Interfaces/Product';
import Certificate from '../../../../Models/Interfaces/Certificate';
import ActionTypes from '../../../../Constants/ActionTypes';
import WeightUnits from '../../../../Constants/WeightUnits';
import CertificateTypes from '../../../../Constants/CertificateTypes';
import CertificateStatuses from '../../../../Constants/CertificateStatuses';
import CertificateProductInfo from '../CertificateProductInfo/CertificateProductInfo';
import ActionConfirmationModal from '../ActionConfirmationModal/ActionConfirmationModal';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { confirmCertificate, getCertificatePdfById, getCertificatePdfPreviewById, combineCertificates, getCertificates, getCombinedCertificatePdfs, getProductPdfs } from '../../../../Services/VeidivottordAPIService';
import { Card, CardData, CardLabel, CertificateHeader, CertificateOverviewWrapper, ChipContainer, ContainerCard, RectificationHistoryTitle, RowContainer, StatusContainer, StyledBox, TitleFont } from './CertificateOverview.styled';
import { Difference, Fingerprint } from '@mui/icons-material';
import { debounce, set } from 'lodash';
import getNiceErrorMessage from '../../../../Models/ErrorModels/TracesErrorMessage';
import ReferenceData from '../../../../Models/Interfaces/ReferenceData';
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import DescriptionIcon from "@mui/icons-material/Description";
import FactCheckIcon from "@mui/icons-material/FactCheck";

interface CertificateOverviewProps {
  selectedCertificate: Certificate | null;
  selectedProduct: Product | undefined;
  toggleProductModal: (actionType?: string) => void;
  toggleLandingModal: () => void;
  toggleCertificateModal: (actionType?: string, inputToFocus?: string) => void;
  setLoadingState: (loadingState: boolean) => void;
  openSnackbar: (statusCode: number, message?: string) => void;
  handleProductSelect: (rowId: GridRowId | undefined) => void;
  fetchCertificate: (certificateId: number, onConfirm?: boolean) => Promise<Certificate | undefined>;
  fetchCertificates: (fromDate?: Date, toDate?: Date) => void;
  modalActionType: string | undefined;
  windowDimensions: { width: number, height: number };
  navigationValue: number;
  setNavigationValue: (navigationValue: number) => void;
  fromDate?: Date;
  toDate?: Date;
  referenceData: ReferenceData | undefined;
}

const CertificateOverview: FC<CertificateOverviewProps> = (props) => {

  const [voidSelectionModalOpen, setVoidSelectionModalOpen] = useState(false);
  const [copySelectionModalOpen, setCopySelectionModalOpen] = useState(false);
  const [rectificationHistoryModalOpen, setRectificationHistoryModalOpen] = useState(false);
  const [combineModalOpen, setCombineModalOpen] = useState(false);
  const [certificateIdToCombine, setCertificateIdToCombine] = useState<number | undefined>(undefined);
  const [confirmButtonTooltip, setConfirmButtonTooltip] = useState('Staðfesta vottorð.');
  const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(true);
  const [value, setValue] = useState('1');
  const [searchValue, setSearchValue] = useState<string>('');
  const [certificateSearchList, setCertificateSearchList] = useState<Certificate[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const toggleVoidSelectionModal = () => setVoidSelectionModalOpen(!voidSelectionModalOpen);
  const toggleCopySelectionModal = () => setCopySelectionModalOpen(!copySelectionModalOpen);
  const toggleRectificationHistoryModalOpen = () => setRectificationHistoryModalOpen(!rectificationHistoryModalOpen);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const handleConfirmCertificate = async () => {
    try {
      const response = await confirmCertificate(props.setLoadingState, props.selectedCertificate?.id);
      const certificate = await props.fetchCertificate(props.selectedCertificate?.id ? props.selectedCertificate?.id : 0, true);
      props.openSnackbar(response.status);
      downloadCertificatePdf(certificate?.number);
    } catch (error: any) {
      const { status, message } = getNiceErrorMessage(error);
      if (status === 400) {
        props.openSnackbar(400, message);
      } else {
        props.openSnackbar(error.response?.status);
      }
    }
  };

  const openCertificatePdf = async () => {
    const response = await getCertificatePdfById(props.setLoadingState, props.selectedCertificate?.id ? props.selectedCertificate?.id : 0);
    const pdfBlob = new Blob([response], { type: 'application/pdf' });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  };

  const openCertificatePreviewPdf = async () => {
    const response = await getCertificatePdfPreviewById(props.setLoadingState, props.selectedCertificate?.id ? props.selectedCertificate?.id : 0);
    const pdfBlob = new Blob([response], { type: 'application/pdf' });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  }

  const downloadCertificatePdf = async (certificateNumber?: string) => {
    const response = await getCertificatePdfById(props.setLoadingState, props.selectedCertificate?.id ? props.selectedCertificate?.id : 0);
    const pdfBlob = new Blob([response], { type: 'application/pdf' });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `catch_certificate_${certificateNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllProcessingStatementPdfs = async () => {
    if (!props.selectedCertificate?.id) return;
    const certificateNumber =
      props.selectedCertificate?.number ?? props.selectedCertificate.id;
    const response = await getProductPdfs(
      props.setLoadingState,
      props.selectedCertificate.id
    );
    const pdfBlob = new Blob([response], { type: "application/pdf" });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `processing_statements_${certificateNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadCombinedCertificatePdfs = async () => {
    const response = await getCombinedCertificatePdfs(props.setLoadingState, props.selectedCertificate?.id ? props.selectedCertificate?.id : 0);
    const pdfBlob = new Blob([response], { type: 'application/pdf' });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `catch_and_processing_certificates_${props.selectedCertificate?.number}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    confirmButtonStatus();
  }, [props.selectedCertificate?.products]);

  const getConnectedLandingCount = (product: Product) => {
    // Count by fishTypeId if certificate is from old system, otherwise count by productId.
    return props?.selectedCertificate?.landings
      ? props?.selectedCertificate?.fromDotNetAPI === false
        ? props?.selectedCertificate?.landings.filter(landing => landing.fishType.id === product?.fishType?.id)?.length
        : props?.selectedCertificate?.landings.filter(landing => landing.productId === product?.id)?.length
      : 0
  }

  const confirmButtonStatus = () => {

    // Calculate weight of all landings and products in KG.
    const lbToKgConversion = 0.45359237;
    const ozToKgConversion = 0.028349523;
    const productWeightUnit = props.selectedCertificate?.products?.length ? props.selectedCertificate?.products[0]?.weightUnit?.id : 0;
    const landingWeightSumKg = props.selectedCertificate?.landings?.length ? props.selectedCertificate?.landings?.reduce((total, landing) => total + (landing?.landedAmount), 0) : 0;

    // For each product, check if the product weight is greater than the weight of connected landings, respective to the weight unit.
    // We also sum up the total weight of all products respective to the weight unit.
    let allProductsWeightSumKg = 0;
    const productsWithGreaterProductWeightThanLandings: string[] = [];
    props.selectedCertificate?.products?.forEach(product => {
      const productWeight = product?.nettoWeight;
      const landingWeight = props.selectedCertificate?.landings.length ? props.selectedCertificate?.landings?.filter(landing => landing.productId === product?.id)?.reduce((total, landing) => total + (landing?.landedAmount), 0) : 0;

      if (productWeightUnit === WeightUnits.G) {
        allProductsWeightSumKg += productWeight / 1000;
        if (productWeight / 1000 > landingWeight) {
          productsWithGreaterProductWeightThanLandings.push(product?.fishType?.description ? product?.fishType?.description : '');
        }
      } else if (productWeightUnit === WeightUnits.KG) {
        allProductsWeightSumKg += productWeight;
        if (productWeight > landingWeight) {
          productsWithGreaterProductWeightThanLandings.push(product?.fishType?.description ? product?.fishType?.description : '');
        };
      } else if (productWeightUnit === WeightUnits.LB) {
        allProductsWeightSumKg += productWeight * lbToKgConversion;
        if (productWeight * lbToKgConversion > landingWeight) {
          productsWithGreaterProductWeightThanLandings.push(product?.fishType?.description ? product?.fishType?.description : '');
        }
      } else if (productWeightUnit === WeightUnits.OZ) {
        allProductsWeightSumKg += productWeight * ozToKgConversion;
        if (productWeight * ozToKgConversion > landingWeight) {
          productsWithGreaterProductWeightThanLandings.push(product?.fishType?.description ? product?.fishType?.description : '');
        }
      }
    });

    if (props.selectedCertificate?.status?.id ? true : false) {
      setConfirmButtonDisabled(true);
    } else if (props.selectedCertificate?.products?.length === 0) {
      setConfirmButtonTooltip('Vottorð verður að innihalda að minnsta kosti eina vöru.');
      setConfirmButtonDisabled(true);
    } else if (props.selectedCertificate?.products?.find(product => getConnectedLandingCount(product) === 0)) {
      setConfirmButtonTooltip('Hver vara þarf a.m.k. eina tengingu við löndun.');
      setConfirmButtonDisabled(true);
    } else if (props.selectedCertificate?.fromDotNetAPI === false) {
      setConfirmButtonTooltip('Ekki er hægt að staðfesta vottorð úr eldra kerfi.');
      setConfirmButtonDisabled(true);
    } else if (productsWithGreaterProductWeightThanLandings.length > 0) {
      let errorString = 'Vigt vöru er meiri en vigt tengdra landana við vöruna.\n\n';
      errorString += productsWithGreaterProductWeightThanLandings.map(productDescription => productDescription).join('\n');
      setConfirmButtonTooltip(errorString);
      setConfirmButtonDisabled(true);
    } else if (allProductsWeightSumKg > landingWeightSumKg) {
      setConfirmButtonTooltip('Þyngd vöru er meiri en vigt allra tengdra landana.');
      setConfirmButtonDisabled(true);
    } else if (props.selectedCertificate?.typeId === null) {
      setConfirmButtonTooltip('Velja verður áfangastað vottorðs.');
      setConfirmButtonDisabled(true);
    } else if (props.selectedCertificate?.landings?.find(landing => landing.isReversed === true)) {
      setConfirmButtonTooltip('Vottorð inniheldur löndunartengingu sem hefur verið breytt eða bakfærð.');
      setConfirmButtonDisabled(true);
    } else if (props.selectedCertificate?.typeId === CertificateTypes.EU &&
      props.selectedCertificate?.products?.find(product => !product.tarriffCode.cnCodeCC)) {
      setConfirmButtonTooltip('Velja þarf CN númer fyrir allar vörur á evrópuvottorðum.');
      setConfirmButtonDisabled(true);
    } else {
      setConfirmButtonTooltip('Staðfesta vottorð.');
      setConfirmButtonDisabled(false);
    }
  }
  const handleSearch = useMemo(
    () =>
      debounce((searchText: string) => {
        const fetchSearchedCertificates = async () => {
          try {
            const certificates = await getCertificates(props.setLoadingState, undefined, undefined, searchText);

            // Remove the selected certificate from the search results.
            const filteredCertificates = certificates.filter(certificate => certificate.id !== props.selectedCertificate?.id);

            setCertificateSearchList(filteredCertificates);
          } catch (error) {
            console.error('Error fetching searched certificates:', error);
          }
        };

        fetchSearchedCertificates();
      }, 500),
    []
  );

  const hasProcessingOptions =
    (props.selectedCertificate?.products?.length ?? 0) > 0 &&
    props.selectedCertificate?.products?.some((product) => Boolean(product.tracesId));

  const isEditable = props.selectedCertificate?.status?.id === CertificateStatuses.UNCONFIRMED && props.selectedCertificate?.fromDotNetAPI === true;
  const desktopView = props.windowDimensions.width > 768;

  const certificateBaseInfo = (
    <RowContainer>
      <Card
        style={{ cursor: isEditable ? 'pointer' : 'default', width: desktopView ? '' : '100%' }}
        onClick={isEditable ? () => props.toggleCertificateModal(ActionTypes.EDIT_ACTION, 'exporterAddress') : undefined}
      >
        <CardLabel>
          Útflytjandi
          {isEditable && <ModeEditIcon sx={{ fontSize: '1.1em' }} />}
        </CardLabel>
        <CardData style={{ whiteSpace: "pre-line" }}>
          {props.selectedCertificate?.exporterName}
          <br />
          {props.selectedCertificate?.exporterAddress}
          <br />
          {props.selectedCertificate?.exporterCity}
          <br />
          {props.referenceData?.countryCodes?.find(country => country.id === props.selectedCertificate?.exporterCountryCodeId)?.countryName}
          <BusinessIcon />
        </CardData>
      </Card>

      <Card
        style={{ cursor: isEditable ? 'pointer' : 'default', width: desktopView ? '' : '100%' }}
        onClick={isEditable ? () => props.toggleCertificateModal(ActionTypes.EDIT_ACTION, 'receiverName') : undefined}
      >
        <CardLabel>
          <span>
            Móttakandi
            {props.selectedCertificate?.typeId === CertificateTypes.EU &&
              <img
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANBAMAAABbflNtAAAAMFBMVEUAM5kiR4MIOJKqmi0tTnoZQogGNpRyeU8LOpFGXWyvnivCqSEBM5dpcVOXjjibkDZC0N4wAAAASUlEQVQI12NgQAUcDTAWl/hFAygztIEjFMLiBAoxTwAz2UBEAph5WoGBgWkPmkkMLB8YGPgcIKYqMjAIKUDNdS+BmctwUABNDwCX4AkbzIMR+gAAAABJRU5ErkJggg=="
                width="20"
                alt="EU"
                style={{ marginLeft: '0.5em' }}
              />
            }
            {props.selectedCertificate?.typeId === CertificateTypes.USA &&
              <img
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALBAMAAACNJ7BwAAAAMFBMVEXovcLBS1rQd4LUg43jsLZSTnzirbTPdIDEVWNeXYfVhY9lZIxKRHNWVYHUhI7jsLe2+MkaAAAARUlEQVQI12OIORpzVBAMGKJ3zt6aBgZg5qtVQLCOIfbq3avlYMAwc+bOmQwQANLWAQYMSEAJDhgeG8MAwxcXKHBjEIQDADRVI7/3XjC6AAAAAElFTkSuQmCC"
                width="20"
                alt="USA"
                style={{ marginLeft: '0.5em' }}
              />
            }
            {props.selectedCertificate?.typeId === CertificateTypes.JAPAN &&
              <img
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANBAMAAABbflNtAAAAG1BMVEX///+8AC3EH0bJM1fXaIL66+7hj6Ltusa9BzJnmT8QAAAAL0lEQVQI12NgwAnK0mGsYEFBUyjTUVBQBMpUFBQUgjIbBQUlMEWR1CKZgGQuJgAAgvAFYoodbs4AAAAASUVORK5CYII="
                width="20"
                alt="JAPAN"
                style={{ marginLeft: '0.5em' }}
              />
            }
          </span>
          {isEditable && <ModeEditIcon sx={{ fontSize: '1.1em' }} />}
        </CardLabel>
        <CardData style={{ whiteSpace: "pre-line" }}>
          {props?.selectedCertificate?.receiverName}
          <br />
          {props?.selectedCertificate?.receiverAddress}
          <br />
          {props?.selectedCertificate?.receiverCity}
          <br />
          {props.referenceData?.countryCodes?.find(country => country.id === props.selectedCertificate?.receiverCountryCodeId)?.countryName}
          <br />
          {props?.selectedCertificate?.receiverEORI && `${props.selectedCertificate?.receiverEORI}`}
          <BusinessIcon />
        </CardData>
      </Card>

      <Card
        style={{ cursor: isEditable ? 'pointer' : 'default', width: desktopView ? '' : '100%' }}
        onClick={isEditable ? () => props.toggleCertificateModal(ActionTypes.EDIT_ACTION, 'referenceNumber') : undefined}
      >
        <CardLabel>
          Tilvísunarnúmer
          {isEditable && <ModeEditIcon sx={{ fontSize: '1.1em' }} />}
        </CardLabel>
        <CardData>
          {props?.selectedCertificate?.referenceNumber ? props?.selectedCertificate?.referenceNumber : ' - '} <ContentPasteIcon />
        </CardData>
      </Card>

      {props.selectedCertificate?.transportingShip &&
        <Card
          style={{ cursor: isEditable ? 'pointer' : 'default', width: desktopView ? '' : '100%' }}
          onClick={isEditable ? () => props.toggleCertificateModal(ActionTypes.EDIT_ACTION, 'transportingShip') : undefined}
        >
          <CardLabel>
            Flutningsskip
            {isEditable && <ModeEditIcon sx={{ fontSize: '1.1em' }} />}
          </CardLabel>
          <CardData>
            <Box>
              {props.selectedCertificate?.transportingShip}
              {props.selectedCertificate?.transportingShipImoNumber && (
                <Box component="span" sx={{ display: 'block' }}>
                  IMO: {props.selectedCertificate.transportingShipImoNumber}
                </Box>
              )}
              {props.selectedCertificate?.voyageNumber && (
                <Box component="span" sx={{ display: 'block' }}>
                  Ferðanúmer: {props.selectedCertificate.voyageNumber}
                </Box>
              )}
            </Box>
            <DirectionsBoatFilledIcon />
          </CardData>
        </Card>
      }

      {props.selectedCertificate?.containerNumber &&
        <Card
          style={{ cursor: isEditable ? 'pointer' : 'default', width: desktopView ? '' : '100%' }}
          onClick={isEditable ? () => props.toggleCertificateModal(ActionTypes.EDIT_ACTION, 'containerNumber') : undefined}
        >
          <CardLabel>
            Gámanúmer
            {isEditable && <ModeEditIcon sx={{ fontSize: '1.1em' }} />}
          </CardLabel>
          <CardData>
            {props.selectedCertificate?.containerNumber}
            {props.selectedCertificate?.containerSealNumber ? ` / ${props.selectedCertificate.containerSealNumber}` : ''} <GridOnIcon />
          </CardData>
        </Card>
      }

      {props.selectedCertificate?.roadVehicleNumber &&
        <Card
          style={{ cursor: isEditable ? 'pointer' : 'default', width: desktopView ? '' : '100%' }}
          onClick={isEditable ? () => props.toggleCertificateModal(ActionTypes.EDIT_ACTION, 'roadVehicleNumber') : undefined}
        >
          <CardLabel>
            Bílnúmer/Vagnnúmer
            {isEditable && <ModeEditIcon sx={{ fontSize: '1.1em' }} />}
          </CardLabel>
          <CardData>
            {props.selectedCertificate?.roadVehicleNumber} <LocalShippingIcon />
          </CardData>
        </Card>
      }

      {props.selectedCertificate?.flightNumber &&
        <Card
          style={{ cursor: isEditable ? 'pointer' : 'default', width: desktopView ? '' : '100%' }}
          onClick={isEditable ? () => props.toggleCertificateModal(ActionTypes.EDIT_ACTION, 'flightNumber') : undefined}
        >
          <CardLabel>
            Flugnúmer
            {isEditable && <ModeEditIcon sx={{ fontSize: '1.1em' }} />}
          </CardLabel>
          <CardData>
            {props.selectedCertificate?.flightNumber} <FlightIcon />
          </CardData>
        </Card>
      }
      <Card
        style={{ cursor: isEditable ? 'pointer' : 'default', width: desktopView ? '' : '100%' }}
        onClick={isEditable ? () => props.toggleCertificateModal(ActionTypes.EDIT_ACTION, 'trackingNumber') : undefined}
      >
        <CardLabel>
          Farmbréfsnúmer
          {isEditable && <ModeEditIcon sx={{ fontSize: '1.1em' }} />}
        </CardLabel>
        <CardData>
          {props.selectedCertificate?.trackingNumber ? props.selectedCertificate?.trackingNumber : '-'} <ManageSearchIcon />
        </CardData>
      </Card>
    </RowContainer>
  );

  const certificateProductInfo = (
    <React.Fragment>
      {!desktopView &&
        <span>
          <span style={{ display: 'flex', justifyContent: 'space-between' }}>
            <TitleFont style={{ margin: 0 }}>Vörur</TitleFont>
            <Button
              variant="outlined"
              size="large"
              endIcon={<AddIcon />}
              onClick={() => props.toggleProductModal(ActionTypes.CREATE_ACTION)}
              disabled={(props.selectedCertificate?.status?.id || props.selectedCertificate?.fromDotNetAPI === false || props.selectedCertificate?.id === undefined) ? true : false}
            >
              Bæta við vöru
            </Button>
          </span>


          {(props.selectedCertificate?.products?.length === 0 || props.selectedCertificate?.id === undefined) &&
            <Card style={{ marginTop: '1em' }} onClick={() => props.toggleProductModal(ActionTypes.CREATE_ACTION)}>
              <CardData>
                Engar vörur skráðar.
              </CardData>
            </Card>
          }
        </span>
      }

      <CertificateProductInfo
        selectedCertificate={props.selectedCertificate}
        toggleProductModal={props.toggleProductModal}
        toggleLandingModal={props.toggleLandingModal}
        selectedProduct={props.selectedProduct}
        handleProductSelect={props.handleProductSelect}
        setLoadingState={props.setLoadingState}
        fetchCertificate={props.fetchCertificate}
        openSnackbar={props.openSnackbar}
        windowDimensions={props.windowDimensions}
      />
    </React.Fragment>
  );

  const certificateHeader = (
    <CertificateHeader>
      <StatusContainer style={{ paddingBottom: desktopView ? '' : '1em', flexDirection: desktopView ? 'row' : 'column' }}>
        <span>
          <TitleFont
            style={{
              width: desktopView ? '' : '100%',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: 1,
                rowGap: 0.5
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  whiteSpace: 'nowrap',
                  minWidth: 0
                }}
              >
                Veiðivottorð {props?.selectedCertificate?.number ? `#${props.selectedCertificate.number}` : ''}
              </Box>
            </Box>
          </TitleFont>

          <ChipContainer>
            <Chip
              icon={props.selectedCertificate?.status?.id === CertificateStatuses.CONFIRMED ? <GppGoodIcon />
                : props.selectedCertificate?.status?.id === CertificateStatuses.VOID ? <GppBadIcon />
                  : <GppMaybeIcon />
              }
              color={props.selectedCertificate?.status?.id === CertificateStatuses.CONFIRMED ? 'success'
                : props.selectedCertificate?.status?.id === CertificateStatuses.VOID ? 'error'
                  : 'default'
              }
              size="small"
              label={props.selectedCertificate?.status?.description}
            />

            <Chip
              icon={<AccessTimeFilledIcon />}
              color={'info'}
              size="small"
              label={dayjs(new Date(props.selectedCertificate?.dateCreated ? props.selectedCertificate?.dateCreated : '')).format('DD/MM/YYYY')}
            />
            {(props?.selectedCertificate?.id !== undefined && props?.selectedCertificate?.fromDotNetAPI === false) &&
              <Tooltip title="Upplýsingar koma úr eldra kerfi. ">
                <Chip
                  icon={<WarningIcon />}
                  color='warning'
                  size='small'
                  label='Frá eldra kerfi' />
              </Tooltip>
            }
            {(props?.selectedCertificate?.id !== undefined && props?.selectedCertificate?.fromExternalClient === true) &&
              <Tooltip title="Upplýsingar koma úr vefþjónustu. ">
                <Chip
                  icon={<ApiIcon />}
                  color='primary'
                  size='small'
                  label={`Vefþjónusta`} />
              </Tooltip>
            }

            {(props?.selectedCertificate?.id !== undefined && props?.selectedCertificate?.fromExternalClient === true) &&
              <Tooltip title="Gagnagrunns ID">
                <Chip
                  icon={<Fingerprint />}
                  color='primary'
                  size='small'
                  label={`ID-${props.selectedCertificate?.id}`} />
              </Tooltip>
            }

            {(props?.selectedCertificate?.rectificationHistory?.length !== undefined && props?.selectedCertificate?.rectificationHistory?.length > 0) &&
              <Tooltip title="Skoða leiðréttingasögu.">
                <Chip
                  onClick={toggleRectificationHistoryModalOpen}
                  icon={<VisibilityIcon />}
                  color='primary'
                  size='small'
                  label='Leiðréttingasaga' />
              </Tooltip>
            }
            {props?.selectedCertificate?.tracesId && (
              <Tooltip title="Traces ID">
                <Chip
                  icon={<VerifiedIcon />}
                  color='secondary'
                  size='small'
                  label={props.selectedCertificate?.tracesId || ''}
                  onClick={() => {
                    navigator.clipboard.writeText(props.selectedCertificate?.tracesId || '');
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </Tooltip>
            )}
          </ChipContainer>
        </span>

        <Stack direction="row" sx={{ flexFlow: desktopView ? 'row wrap' : 'column', justifyContent: 'start', gap: '1em', maxHeight: desktopView ? '2em' : '1' }}>
          {/* Óstaðfest vottorð */}
          {(isEditable) &&
            <Tooltip title={<div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{confirmButtonTooltip}</div>}>
              <span>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleConfirmCertificate}
                  startIcon={<GppGoodIcon />}
                  disabled={confirmButtonDisabled}
                  style={{ width: desktopView ? '' : '100%' }}
                >
                  Staðfesta
                </Button>
              </span>
            </Tooltip>
          }
          {(isEditable && props.selectedCertificate?.typeId !== CertificateTypes.EU) &&
            <Tooltip title={<div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>Forskoða PDF</div>}>
              <span>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<RemoveRedEyeIcon />}
                  onClick={() => openCertificatePreviewPdf()}
                  style={{ width: desktopView ? '' : '100%' }}
                >
                  Forskoða
                </Button>
              </span>
            </Tooltip>
          }

          {props.selectedCertificate?.fromExternalClient &&
            <>
              <Tooltip title="Sameina vottorð.">
                <span>
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<ContentCopy />}
                    onClick={() => setCombineModalOpen(true)}
                    style={{ width: desktopView ? '' : '100%' }}
                  >
                    Sameina
                  </Button>
                </span>
              </Tooltip>
              <Modal
                open={combineModalOpen}
                onClose={() => setCombineModalOpen(false)}
              >
                <StyledBox>
                  <TitleFont>Sameina vottorð</TitleFont>
                  <Box
                    className="combine-modal-content"
                    style={{
                      marginTop: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 24,
                    }}
                  >
                    {/* Search and select certificate to combine */}
                    <Box
                      className="combine-modal-search"
                      style={{
                        width: '100%',
                        marginBottom: 16,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <SearchIcon
                        style={{
                          position: 'absolute',
                          left: 16,
                          color: '#757575',
                          zIndex: 1,
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Leita að vottorði eftir ID"
                        value={searchValue}
                        style={{
                          padding: '12px 16px 12px 44px',
                          borderRadius: '6px',
                          border: '1.5px solid #bfc8e6',
                          width: '100%',
                          boxSizing: 'border-box',
                          fontSize: '1rem',
                          background: '#f8fafc',
                          transition: 'border 0.2s',
                          outline: 'none',
                        }}
                        onFocus={e => (e.target.style.border = '1.5px solid #1976d2')}
                        onBlur={e => (e.target.style.border = '1.5px solid #bfc8e6')}
                        onChange={e => {
                          const trimmedValue = e.target.value.trimStart();
                          setSearchValue(trimmedValue);
                          if (trimmedValue.length === 0) {
                            handleSearch('');
                          } else if (trimmedValue.length >= 2) {
                            const searchInput = trimmedValue.startsWith('ID-') ? trimmedValue : `ID-${trimmedValue}`;
                            handleSearch(searchInput);
                          }
                        }}
                      />
                      {searchValue && (
                        <IconButton
                          size="small"
                          style={{
                            position: 'absolute',
                            right: 8,
                            color: '#757575',
                            zIndex: 2,
                          }}
                          onClick={() => {
                            setSearchValue('');
                            handleSearch('');
                          }}
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                    </Box>

                    <Box
                      className="combine-modal-list"
                      style={{
                        maxHeight: 300,
                        overflowY: 'auto',
                        border: '1.5px solid #bfc8e6',
                        borderRadius: '6px',
                        background: '#f8fafc',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        marginBottom: 8,
                      }}
                    >
                      {certificateSearchList.length === 0 && searchValue.length >= 2 ? (
                        <Box style={{ padding: 16, color: '#757575', textAlign: 'center' }}>
                          Engin vottorð fundust.
                        </Box>
                      ) : (
                        certificateSearchList.map(certificate => (
                          <Box
                            key={certificate.id}
                            className={`combine-modal-list-item${certificateIdToCombine === certificate.id ? ' selected' : ''}`}
                            style={{
                              padding: '12px 16px',
                              backgroundColor: certificateIdToCombine === certificate.id ? '#1976d2' : 'transparent',
                              color: certificateIdToCombine === certificate.id ? '#fff' : '#212121',
                              cursor: 'pointer',
                              borderBottom: '1px solid #e0e7ff',
                              transition: 'background 0.15s',
                              fontWeight: certificateIdToCombine === certificate.id ? 600 : 400,
                              fontSize: '0.98rem',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                            }}
                            onClick={() => setCertificateIdToCombine(certificate.id)}
                          >
                            <span style={{ fontWeight: 500 }}>
                              Veiðivottorð #{certificate.number}
                            </span>
                            <span style={{ color: certificateIdToCombine === certificate.id ? '#e0e7ff' : '#64748b' }}>
                              {certificate.exporterName}
                            </span>
                            <span style={{ color: certificateIdToCombine === certificate.id ? '#e0e7ff' : '#64748b', fontSize: '0.93em' }}>
                              {dayjs(new Date(certificate.dateCreated ?? '')).format('DD/MM/YYYY')}
                            </span>
                            <span style={{ color: certificateIdToCombine === certificate.id ? '#e0e7ff' : '#64748b', fontSize: '0.93em' }}>
                              ID-{certificate.id}
                            </span>
                          </Box>
                        ))
                      )}
                    </Box>

                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!certificateIdToCombine}
                      style={{
                        alignSelf: 'flex-end',
                      }}
                      onClick={async () => {
                        setCombineModalOpen(false);
                        const response = await combineCertificates(
                          props.setLoadingState,
                          props.selectedCertificate?.id,
                          certificateIdToCombine
                        );
                        props.fetchCertificates(props.fromDate, props.toDate);
                        props.fetchCertificate(response.data);
                        props.openSnackbar(response.status);
                        setCertificateIdToCombine(undefined);
                      }}
                    >
                      Sameina
                    </Button>
                  </Box>
                </StyledBox>
              </Modal>
            </>
          }
          {(props.selectedCertificate?.status?.id === CertificateStatuses.CONFIRMED && props.selectedCertificate?.fromDotNetAPI === true) &&
            <Tooltip title="Leiðrétta vottorð.">
              <span>
                <Button
                  variant="outlined"
                  color="info"
                  onClick={toggleVoidSelectionModal}
                  startIcon={<ModeEditIcon />}
                  disabled={!!props.selectedCertificate.rectificationHistory.find(certificate => certificate.status.id === CertificateStatuses.UNCONFIRMED)}
                  style={{ width: desktopView ? '' : '100%' }}
                >
                  Leiðrétta
                </Button>
              </span>
            </Tooltip>
          }
          <Tooltip title={props.selectedCertificate?.typeId === CertificateTypes.EU && props.selectedCertificate?.products?.find(product => !product.tarriffCode.cnCodeCC) !== undefined ? "Ekki er hægt að afrita þetta vottorð." : "Afrita vottorð."}>
            <span>
              <Button
                variant="outlined"
                color="info"
                startIcon={<ContentCopy />}
                onClick={toggleCopySelectionModal}
                style={{ width: desktopView ? '' : '100%' }}
                disabled={props.selectedCertificate?.typeId === CertificateTypes.EU && props.selectedCertificate?.products?.find(product => !product.tarriffCode.cnCodeCC) !== undefined}
              >
                Afrita
              </Button>
            </span>
          </Tooltip>
          {((props.selectedCertificate?.status?.id === CertificateStatuses.CONFIRMED || props.selectedCertificate?.status?.id === CertificateStatuses.VOID) && props?.selectedCertificate?.fromDotNetAPI === true) &&
            <Tooltip title="Skoða PDF">
              <span>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<RemoveRedEyeIcon />}
                  onClick={() => openCertificatePdf()}
                  style={{ width: desktopView ? '' : '100%' }}
                >
                  Skoða
                </Button>
              </span>
            </Tooltip>
          }

          {((props.selectedCertificate?.status?.id === CertificateStatuses.CONFIRMED ||
            props.selectedCertificate?.status?.id === CertificateStatuses.VOID) &&
            props?.selectedCertificate?.fromDotNetAPI === true) && (
              <React.Fragment>
                <Tooltip title="Hlaða niður">
                  <span>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<SaveAltIcon />}
                      endIcon={hasProcessingOptions ? <ArrowDropDownIcon /> : null}
                      onClick={(e) => {
                        if (hasProcessingOptions) {
                          setAnchorEl(e.currentTarget);
                        } else {
                          downloadCertificatePdf(props.selectedCertificate?.number);
                        }
                      }}
                      style={{ width: desktopView ? "" : "100%" }}
                    >
                      Hlaða niður
                    </Button>
                  </span>
                </Tooltip>

                {hasProcessingOptions && (
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                  >
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        downloadCertificatePdf(props.selectedCertificate?.number);
                      }}
                    >
                      <ListItemIcon>
                        <DescriptionIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Veiðivottorði</ListItemText>
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        downloadAllProcessingStatementPdfs();
                      }}
                    >
                      <ListItemIcon>
                        <FactCheckIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Vinnsluvottorðum</ListItemText>
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        downloadCombinedCertificatePdfs();
                      }}
                    >
                      <ListItemIcon>
                        <Difference fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Veiði- og vinnsluvottorðum</ListItemText>
                    </MenuItem>
                  </Menu>
                )}
              </React.Fragment>
            )}
        </Stack>
      </StatusContainer>
    </CertificateHeader >
  );

  return (
    <CertificateOverviewWrapper
      style={{
        width: desktopView ? (props.windowDimensions.width < 1200 ? '60%' : 'calc(75% - 1em)') : '100%',
        margin: desktopView ? '1em 1em 1em 0em' : '0',
      }}>

      {(desktopView || (props.navigationValue === 1 && !desktopView)) &&
        (
          <ContainerCard
            style={{
              overflow: 'auto',
              minHeight: desktopView ? 'calc(100% - 52px)' : '',
              height: desktopView ? '' : props.navigationValue === 1 ? 'calc(100% - 52px)' : '',
              flex: desktopView ? '1 1 1' : '',
              borderRadius: desktopView ? '0.75em' : '0',
              boxShadow: desktopView ? '' : 'none',
              border: desktopView ? '' : 'none',
              padding: desktopView ? '1em' : '',
            }}>

            {/* Desktop view */}

            {(props.selectedCertificate?.id !== undefined && desktopView) && certificateHeader}
            {(props.selectedCertificate?.id !== undefined && desktopView) && certificateBaseInfo}
            {desktopView && certificateProductInfo}

            {/* Mobile view */}
            {!desktopView &&
              (
                <TabContext value={value}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}>
                    <TabList
                      onChange={handleTabChange}
                      sx={{
                        '& .MuiTabs-flexContainer': {
                          justifyContent: 'space-evenly',
                        }
                      }}>
                      <Tab label="Grunnupplýsingar" value="1" />
                      <Tab label="Vöruupplýsingar" value="2" />
                    </TabList>
                  </Box>
                  <TabPanel value="1">
                    {props.selectedCertificate?.id !== undefined && certificateHeader}
                    {props.selectedCertificate?.id !== undefined && certificateBaseInfo}
                    {props.selectedCertificate?.id === undefined && "Veldu vottorð til að skoða upplýsingar."}
                  </TabPanel>
                  <TabPanel value="2">
                    {certificateProductInfo}
                  </TabPanel>
                </TabContext>
              )
            }
          </ContainerCard>
        )}

      <ActionConfirmationModal
        actionConfirmationModalOpen={voidSelectionModalOpen || copySelectionModalOpen}
        certificateStatusId={props.selectedCertificate?.status?.id}
        fetchCertificate={props.fetchCertificate}
        fetchCertificates={props.fetchCertificates}
        openSnackbar={props.openSnackbar}
        setLoadingState={props.setLoadingState}
        selectedCertificate={props.selectedCertificate}
        voidSelectionModalOpen={voidSelectionModalOpen}
        copySelectionModalOpen={copySelectionModalOpen}
        toggleVoidSelectionModal={toggleVoidSelectionModal}
        toggleCopySelectionModal={toggleCopySelectionModal}
      />
      <Modal
        open={rectificationHistoryModalOpen}
        onClose={() => toggleRectificationHistoryModalOpen()}
      >
        <StyledBox>
          <RectificationHistoryTitle>
            Leiðréttingasaga
          </RectificationHistoryTitle>
          {rectificationHistoryModalOpen === true &&
            <Timeline
              sx={{
                [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 0,
                },
              }}
            >
              {props.selectedCertificate?.rectificationHistory?.slice(0).reverse().map((rectifiedCertificate, index) => {
                return (
                  <TimelineItem key={index}>
                    <TimelineSeparator>
                      <TimelineDot />
                      {props.selectedCertificate?.rectificationHistory && index !== props.selectedCertificate?.rectificationHistory?.length - 1 &&
                        <TimelineConnector />
                      }
                    </TimelineSeparator>
                    <TimelineContent>
                      <Card onClick={() => {
                        setRectificationHistoryModalOpen(false);
                        if (rectifiedCertificate?.id) {
                          props.fetchCertificate(rectifiedCertificate?.id);
                        }
                      }} style={{ cursor: 'pointer' }}>
                        <CardData>
                          <span>
                            Veiðivottorð {rectifiedCertificate?.number ? '#' + rectifiedCertificate?.number : ''}
                            <br></br>
                            {dayjs(new Date(rectifiedCertificate?.dateCreated ? rectifiedCertificate?.dateCreated : '')).format('DD/MM/YYYY')}
                          </span>
                          <Chip
                            icon={rectifiedCertificate?.status?.id === CertificateStatuses.CONFIRMED ? <GppGoodIcon />
                              : rectifiedCertificate?.status?.id === CertificateStatuses.VOID ? <GppBadIcon />
                                : <GppMaybeIcon />
                            }
                            color={rectifiedCertificate?.status?.id === CertificateStatuses.CONFIRMED ? 'success'
                              : rectifiedCertificate?.status?.id === CertificateStatuses.VOID ? 'error'
                                : 'default'
                            }
                            size="small"
                            label={rectifiedCertificate?.status?.description} />
                        </CardData>
                      </Card>

                    </TimelineContent>
                  </TimelineItem>);
              })}
            </Timeline>
          }
        </StyledBox>
      </Modal>
    </CertificateOverviewWrapper>
  );
}

export default CertificateOverview;
